import request from "@/lib/api/handler";
import { useAuth0 } from "@auth0/auth0-react";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";

import { ConversationMessage } from "@/queries/chat/user-chat";
import { ConversationSummary } from "@/queries/chat/user-conversation";

export const useStartChatConversation = (
  candidateId: string,
  companyId: string,
  jobOpeningId?: string,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (message: string) => {
      const token = await getAccessTokenSilently();

      return request<{
        message: ConversationMessage;
        conversation: ConversationSummary;
      }>({
        method: "POST",
        path: "/api/chat/start",
        token,
        body: JSON.stringify({ candidateId, companyId, jobOpeningId, message }),
      });
    },
    onSuccess: (data) => {
      qc.setQueryData(
        QueryKeys.chatMessage(data.conversation.id),
        (oldData?: InfiniteData<ConversationMessage[]>) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            pages: [
              [data.message, ...(oldData.pages[0] ?? [])],
              ...oldData.pages.slice(1),
            ],
          };
        },
      );

      qc.setQueryData(
        QueryKeys.chatConversations,
        (oldData?: ConversationSummary[]) => {
          const update = [...(oldData ?? [])];

          const idx = update.findIndex(({ id }) => id === data.conversation.id);

          if (idx !== -1) {
            update[idx] = data.conversation;
          } else {
            update.push(data.conversation);
          }

          return update;
        },
      );
    },
  });

  return {
    startConversation: mutate,
    isStartingConversation: isPending,
    startConversationError: error,
  };
};
